import { BaseModal } from '@byecode/ui'
import type { FC, MouseEvent, PointerEvent } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { useApplicationContext } from '../../contexts'
import { isMobileDevice } from '../../utils'
import { FileCarousel } from './FileCarousel'
import type { FilePreviewerProps } from './types'
import { shouldTriggerWechatImageLongPress } from './wechatImageLongPress'
import { canWechatNativePreview, wechatPreviewFile } from './wechatPreview'

const PreviewerModal = styled(BaseModal)<{ isMobile: boolean }>`
    .byecode-Modal-modal {
        width: 100vw;
        height: 100vh;
        border-radius: 0;
        border: none;
        box-shadow: none;
        ${({ isMobile }) =>
            isMobile &&
            css`
                background-color: #000;
            `}
    }

    .byecode-Modal-body {
        padding: 0;
    }
`

// 文件预览弹窗主体
export const FilePreviewer: FC<FilePreviewerProps> = ({
    fileList,
    defaultIndex = 0,
    disableDownload,
    opened: propOpened,
    target,
    onClose,
    withControls
}) => {
    const [opened, setOpened] = useState(propOpened ?? false)

    useEffect(() => {
        setOpened(propOpened ?? false)
    }, [propOpened])

    const isMobile = isMobileDevice()
    const { openWechatDocument } = useApplicationContext()
    const wechatPreviewKeyRef = useRef<string>()
    const [wechatFallbackKey, setWechatFallbackKey] = useState<string>()

    const [currentIndex, setCurrentIndex] = useState(defaultIndex)
    const pressStartedAtRef = useRef(0)

    const handleMobilePointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
        pressStartedAtRef.current = event.timeStamp
    }, [])

    const handleMobileClick = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            if (shouldTriggerWechatImageLongPress({ durationMs: event.timeStamp - pressStartedAtRef.current, moveDistance: 0 })) {
                return
            }
            onClose?.()
        },
        [onClose]
    )

    const previewFile = fileList[defaultIndex]
    const previewFileKey = previewFile ? `${previewFile.url}:${defaultIndex}` : undefined
    const shouldDelegateToWechat = Boolean(
        opened &&
            previewFile &&
            previewFileKey &&
            wechatFallbackKey !== previewFileKey &&
            canWechatNativePreview(previewFile, openWechatDocument)
    )

    useEffect(() => {
        let active = true

        if (!opened) {
            wechatPreviewKeyRef.current = undefined
            setWechatFallbackKey(undefined)
            return () => {
                active = false
            }
        }

        const file = fileList[defaultIndex]
        if (!file) {
            return () => {
                active = false
            }
        }

        const fileKey = `${file.url}:${defaultIndex}`
        if (!canWechatNativePreview(file, openWechatDocument)) {
            wechatPreviewKeyRef.current = undefined
            return () => {
                active = false
            }
        }

        if (wechatPreviewKeyRef.current === fileKey) {
            return () => {
                active = false
            }
        }

        wechatPreviewKeyRef.current = fileKey
        void wechatPreviewFile(fileList, defaultIndex, openWechatDocument).then(handled => {
            if (!active) {
                return
            }
            if (handled) {
                onClose?.()
                return
            }
            wechatPreviewKeyRef.current = undefined
            setWechatFallbackKey(fileKey)
        })

        return () => {
            active = false
        }
    }, [defaultIndex, fileList, onClose, openWechatDocument, opened])

    const title = useMemo(() => {
        if (fileList.length === 0) {
            return null
        }

        if (isMobile) {
            return null
        }

        const filename = fileList[currentIndex]?.name || '未命名文件'
        const page = `(${currentIndex + 1}/${fileList.length})`
        return `${filename} ${page}`
    }, [currentIndex, fileList, isMobile])

    if (shouldDelegateToWechat) {
        return null
    }

    return (
        <PreviewerModal
            isMobile={isMobile}
            target={target}
            zIndex={300}
            title={title}
            open={opened}
            onClose={onClose}
            withCloseIcon={!isMobile}
            onPointerDown={isMobile ? handleMobilePointerDown : undefined}
            onClick={isMobile ? handleMobileClick : undefined}
        >
            <FileCarousel
                withControls={!isMobile && withControls}
                defaultIndex={defaultIndex}
                currentIndex={currentIndex}
                onSlideChange={setCurrentIndex}
                fileList={fileList}
                disableWechatDocumentPreview={wechatFallbackKey === previewFileKey}
            />
        </PreviewerModal>
    )
}
